/**
 * llmClient.ts
 * 前端 DeepSeek 调用层：经服务端代理 /api/ai/llm 发起请求，密钥不进入浏览器。
 * 迁移自旧 hooks/useAIStream.ts —— 仅把上游地址从 api.deepseek.com 换成本站代理，
 * 并去掉浏览器侧的 apiKey，改用 cookie 会话（credentials: 'include'）。
 */

import type { AIMessage, AITool, AIToolCall } from '../types';
import { debugLogger } from '../utils/debugLogger';

const LLM_ENDPOINT = '/api/ai/llm';

/** 用量归因信息：随请求透传给服务端记录 token 消耗 */
export interface LLMUsageMeta {
  sessionUuid?: string;
  bookId?: string;
}

/** 服务端契约使用 snake_case（usage_meta.session_uuid / book_id），序列化时转换字段名 */
export function serializeUsageMeta(meta?: LLMUsageMeta): Record<string, unknown> {
  if (!meta) return {};
  return { usage_meta: { session_uuid: meta.sessionUuid, book_id: meta.bookId } };
}

interface ToolCallResponse {
  finish_reason: 'tool_calls' | 'stop' | 'length';
  content: string;
  tool_calls?: AIToolCall[];
  thinking?: string;
}

function serializeMessages(messages: AIMessage[]) {
  return messages.map((m) => ({
    role: m.role,
    content: m.content,
    ...(m.tool_calls ? { tool_calls: m.tool_calls } : {}),
    ...(m.tool_call_id ? { tool_call_id: m.tool_call_id } : {}),
    ...(m.thinking ? { reasoning_content: m.thinking } : {}),
  }));
}

function handleSessionExpired() {
  document.cookie = 'sw_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  sessionStorage.removeItem('sw_authenticated');
  window.location.hash = '#/';
}

async function extractError(resp: Response): Promise<string> {
  try {
    const j = (await resp.json()) as { error?: string };
    return j.error || `API 错误: ${resp.status}`;
  } catch {
    return `API 错误: ${resp.status}`;
  }
}

/**
 * 流式调用（最终文本输出阶段），分离 reasoning_content / <think> 思考内容。
 * @param reasoningEffort 模型推理力度（模型速率）：low / high / max；缺省用模型默认
 */
export async function callStream(
  model: string,
  messages: AIMessage[],
  signal: AbortSignal,
  onThinking?: (text: string) => void,
  onContent?: (text: string) => void,
  meta?: LLMUsageMeta,
  provider?: string,
  reasoningEffort?: string,
): Promise<{ content: string; thinking: string }> {
  debugLogger.ai('CALL_STREAM_START', { model, provider, messageCount: messages.length, lastRole: messages[messages.length - 1]?.role, reasoningEffort });

  const response = await fetch(LLM_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      model,
      provider,
      messages: serializeMessages(messages),
      temperature: 0.7,
      stream: true,
      // 确保流式响应末尾携带 usage（DeepSeek 默认返回；OpenRouter 需显式请求）
      stream_options: { include_usage: true },
      ...(reasoningEffort ? { reasoning_effort: reasoningEffort } : {}),
      ...serializeUsageMeta(meta),
    }),
    signal,
  });

  if (response.status === 401) {
    handleSessionExpired();
    throw new Error('会话已过期，请重新验证');
  }
  if (!response.ok) {
    const errMsg = await extractError(response);
    debugLogger.aiError('CALL_STREAM_HTTP_ERROR', { model, status: response.status, error: errMsg });
    throw new Error(errMsg);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('无法读取流数据');

  const decoder = new TextDecoder();
  let buffer = '';
  let fullContent = '';
  let fullThinking = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines[lines.length - 1];

      for (let i = 0; i < lines.length - 1; i++) {
        const line = lines[i].trim();
        if (!line || line === 'data: [DONE]') continue;
        if (!line.startsWith('data: ')) continue;

        const dataStr = line.substring(6).trim();
        if (dataStr === '[DONE]') continue;

        try {
          const json = JSON.parse(dataStr);
          const delta = json.choices?.[0]?.delta;
          if (!delta) continue;

          const reasoning = delta.reasoning ?? delta.reasoning_content;
          if (reasoning) {
            fullThinking += reasoning;
            onThinking?.(fullThinking);
          }

          if (delta.content) {
            fullContent += delta.content;

            if (fullContent.includes('<think>') && !fullThinking) {
              const thinkMatch = fullContent.match(/<think>([\s\S]*?)(?:<\/think>|$)/);
              if (thinkMatch) {
                fullThinking = thinkMatch[1].trim();
                onThinking?.(fullThinking);
              }
            }

            const cleanContent = fullContent.replace(/<think>[\s\S]*?<\/think>/g, '').trim();
            onContent?.(cleanContent);
          }
        } catch {
          // 忽略单行 JSON 解析错误
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  const thinkMatch = fullContent.match(/<think>([\s\S]*?)<\/think>/);
  if (thinkMatch && !fullThinking) fullThinking = thinkMatch[1].trim();
  const cleanContent = fullContent.replace(/<think>[\s\S]*?<\/think>/g, '').trim();

  debugLogger.ai('CALL_STREAM_COMPLETE', { model, contentLength: cleanContent.length, thinkingLength: fullThinking.length });
  return { content: cleanContent, thinking: fullThinking };
}

/**
 * 非流式调用 + 工具定义（Agent Loop 工具检测阶段），含 XML 风格回退解析。
 * @param reasoningEffort 模型推理力度（模型速率）：low / high / max；缺省用模型默认
 */
export async function callWithTools(
  model: string,
  messages: AIMessage[],
  tools: AITool[],
  signal: AbortSignal,
  meta?: LLMUsageMeta,
  provider?: string,
  reasoningEffort?: string,
): Promise<ToolCallResponse> {
  debugLogger.ai('CALL_WITH_TOOLS_START', { model, provider, messageCount: messages.length, availableTools: tools.map((t) => t.function.name), reasoningEffort });

  const response = await fetch(LLM_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      model,
      provider,
      messages: serializeMessages(messages),
      tools,
      tool_choice: 'auto',
      temperature: 0.7,
      stream: false,
      ...(reasoningEffort ? { reasoning_effort: reasoningEffort } : {}),
      ...serializeUsageMeta(meta),
    }),
    signal,
  });

  if (response.status === 401) {
    handleSessionExpired();
    throw new Error('会话已过期，请重新验证');
  }
  if (!response.ok) {
    const errMsg = await extractError(response);
    debugLogger.aiError('CALL_WITH_TOOLS_HTTP_ERROR', { model, status: response.status, error: errMsg });
    throw new Error(errMsg);
  }

  const data = (await response.json()) as {
    choices?: Array<{
      finish_reason: string;
      message: { content?: string; tool_calls?: AIToolCall[]; reasoning?: string; reasoning_content?: string };
    }>;
  };

  const choice = data.choices?.[0];
  if (!choice) throw new Error('API 返回数据格式错误');

  const finishReason = choice.finish_reason as 'tool_calls' | 'stop' | 'length';
  let content = choice.message?.content ?? '';
  let toolCalls = choice.message?.tool_calls;
  const thinking = choice.message?.reasoning ?? choice.message?.reasoning_content;

  if (!toolCalls?.length && content) {
    const parsed = parseDeepSeekToolCalls(content);
    if (parsed.toolCalls.length > 0) {
      toolCalls = parsed.toolCalls;
      content = parsed.cleanedContent;
    }
  } else if (toolCalls?.length && content) {
    // 原生 tool_calls 存在时仍清理 content 中的 invoke/DSML/XML 标签
    const parsed = parseDeepSeekToolCalls(content);
    content = parsed.cleanedContent;
  }

  const effectiveFinishReason: 'tool_calls' | 'stop' | 'length' = toolCalls?.length ? 'tool_calls' : finishReason;

  debugLogger.ai('CALL_WITH_TOOLS_COMPLETE', {
    model, finishReason: effectiveFinishReason, toolCallsCount: toolCalls?.length ?? 0, contentLength: content?.length ?? 0,
  });

  return { finish_reason: effectiveFinishReason, content, tool_calls: toolCalls, thinking };
}

// ── DeepSeek XML 风格工具调用解析（三种格式回退） ──────────────────

export function parseDeepSeekToolCalls(content: string): {
  toolCalls: AIToolCall[];
  cleanedContent: string;
} {
  if (!content) return { toolCalls: [], cleanedContent: content };

  const toolCalls: AIToolCall[] = [];
  let cleanedContent = content;

  // 格式1：DeepSeek 特殊分隔符格式
  const dsmlBlockRegex = /<｜tool_calls_begin｜>([\s\S]*?)<｜tool_calls_end｜>/g;
  const dsmlCallRegex = /<｜tool_call_begin｜>function<｜tool_sep｜>([\s\S]*?)<｜tool_sep｜>([\s\S]*?)<｜tool_call_end｜>/g;

  let match;
  let dsmlFound = false;

  while ((match = dsmlBlockRegex.exec(content)) !== null) {
    dsmlFound = true;
    const blockContent = match[1];
    let callMatch;
    while ((callMatch = dsmlCallRegex.exec(blockContent)) !== null) {
      const funcName = callMatch[1].trim();
      const funcArgs = callMatch[2].trim();
      toolCalls.push({ id: `dsml_${Date.now()}_${toolCalls.length}`, type: 'function', function: { name: funcName, arguments: funcArgs } });
    }
  }

  if (dsmlFound) {
    cleanedContent = content.replace(dsmlBlockRegex, '').trim();
    return { toolCalls, cleanedContent };
  }

  // 格式2：<tool_call name="..."> 标签格式
  const xmlCallRegex = /<tool_call\s+name="([^"]+)">([\s\S]*?)<\/tool_call\s*>/g;
  let xmlFound = false;

  while ((match = xmlCallRegex.exec(content)) !== null) {
    xmlFound = true;
    const funcName = match[1].trim();
    const funcArgs = match[2].trim();
    toolCalls.push({ id: `xml_${Date.now()}_${toolCalls.length}`, type: 'function', function: { name: funcName, arguments: funcArgs } });
  }

  if (xmlFound) {
    cleanedContent = content.replace(xmlCallRegex, '').trim();
    return { toolCalls, cleanedContent };
  }

  // 格式3：<invoke name="..."><parameter> 标签格式
  const invokeBlockRegex = /<invoke\s+name="([^"]+)">([\s\S]*?)<\/invoke\s*>/g;
  const paramRegex = /<parameter\s+name="([^"]+)"(?:\s+string="(?:true|false)")?\s*>([\s\S]*?)<\/parameter\s*>/g;
  let invokeFound = false;

  while ((match = invokeBlockRegex.exec(content)) !== null) {
    invokeFound = true;
    const funcName = match[1].trim();
    const paramsBlock = match[2];
    const argsObj: Record<string, unknown> = {};
    let paramMatch;
    while ((paramMatch = paramRegex.exec(paramsBlock)) !== null) {
      const paramName = paramMatch[1].trim();
      const paramValue = paramMatch[2].trim();
      try {
        argsObj[paramName] = JSON.parse(paramValue);
      } catch {
        argsObj[paramName] = paramValue;
      }
    }
    toolCalls.push({ id: `invoke_${Date.now()}_${toolCalls.length}`, type: 'function', function: { name: funcName, arguments: JSON.stringify(argsObj) } });
  }

  if (invokeFound) {
    cleanedContent = content.replace(invokeBlockRegex, '').trim();
    return { toolCalls, cleanedContent };
  }

  return { toolCalls, cleanedContent: content };
}

export async function callLLM(
  model: string,
  messages: AIMessage[],
  temperature = 0.3,
  maxTokens?: number,
  meta?: LLMUsageMeta,
  provider?: string,
  reasoningEffort?: string,
): Promise<string> {
  const response = await fetch(LLM_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      model,
      provider,
      messages: serializeMessages(messages),
      temperature,
      stream: false,
      ...(maxTokens ? { max_tokens: maxTokens } : {}),
      ...(reasoningEffort ? { reasoning_effort: reasoningEffort } : {}),
      ...serializeUsageMeta(meta),
    }),
  });

  if (response.status === 401) {
    handleSessionExpired();
    throw new Error('会话已过期，请重新验证');
  }
  if (!response.ok) {
    const errMsg = await extractError(response);
    throw new Error(errMsg);
  }

  const data = (await response.json()) as {
    choices?: Array<{ message: { content?: string } }>;
  };
  const content = data.choices?.[0]?.message?.content ?? '';
  return content.trim();
}
